import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Log, Permission, PermissionSubject, PermissionVerb } from '@s8l/client-tree-lib';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { ShopifyLicenceModalComponent } from 'src/app/components/shopify-licence-modal/shopify-licence-modal.component';
import { CmsService } from 'src/app/services/cms.service';
import { DurationService } from 'src/app/services/duration.service';
import { ProductLicence, ShopifyOrder, ShopifyPruchase, ShopifyServerService } from 'src/app/services/jsonrpc/shopify-server.service';
import { Role } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';
import { ShopifyPurchaseModalComponent } from 'src/app/components/shopify-purchase-modal/shopify-purchase-modal.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-shopify',
  templateUrl: './shopify.component.html',
  styleUrls: ['./shopify.component.scss']
})
export class ShopifyComponent implements OnInit, OnDestroy {
  public readonly dateTimeFormat = environment.dateTimeFormat;

  public licences: ProductLicence[];
  public products: any[];
  public roles: Role[];
  public orders: ShopifyOrder[];

  private unassignedProducts: any[];
  private mailTemplates: string[];

  public get canEdit(): boolean {
    return this.cms.hasAccess(new Permission(new PermissionSubject('shopify'), PermissionVerb.Write));
  }

  public get canDelete(): boolean {
    return this.cms.hasAccess(new Permission(new PermissionSubject('shopify'), PermissionVerb.Delete));
  }

  constructor(
    public duration: DurationService,
    private cms: CmsService,
    private users: UserService,
    private shopify: ShopifyServerService,
    private modal: NgbModal,
    private toastr: ToastrService
  ) {}

  private roleSubscription: Subscription;

  ngOnInit() {
    this.roleSubscription = this.users.roles.subscribe(r => (this.roles = r));
    return this.load();
  }

  ngOnDestroy(): void {
    this.roleSubscription?.unsubscribe();
  }

  public create() {
    const modalRef = this.modal.open(ShopifyLicenceModalComponent, { size: 'lg' });
    modalRef.componentInstance.roles = this.roles;
    modalRef.componentInstance.mails = this.mailTemplates;
    modalRef.componentInstance.products = this.unassignedProducts;
    modalRef.componentInstance.onSubmit.subscribe((licence: ProductLicence) => {
      this.shopify
        .licenceCreate(licence)
        .then(() => this.toastr.success('Created new shopify licence'))
        .then(() => this.load());
    });
  }

  public update(licence: ProductLicence) {
    const model = this.copy(licence);
    const options = this.unassignedProducts.slice();
    const selected = model.item;
    if (selected) {
      options.unshift(selected);
    }
    const modalRef = this.modal.open(ShopifyLicenceModalComponent, { size: 'lg' });
    modalRef.componentInstance.model = model;
    modalRef.componentInstance.roles = this.roles;
    modalRef.componentInstance.mails = this.mailTemplates;
    modalRef.componentInstance.products = options;
    modalRef.componentInstance.onSubmit.subscribe((licence: ProductLicence) => {
      this.shopify
        .licenceUpdate(licence)
        .then(() => this.toastr.success('Updated shopify licence'))
        .then(() => this.load());
    });
  }

  public purchase(licence: ProductLicence) {
    const input: ShopifyPruchase = {
      licence: licence.uuid,
      sendMail: true,
      title: licence['item'].title,
      recipient: '',
      firstName: '',
      lastName: '',
      language: 'en'
    };
    const modalRef = this.modal.open(ShopifyPurchaseModalComponent, { size: 'lg' });
    modalRef.componentInstance.purchase = input;
    modalRef.componentInstance.onSubmit.subscribe((purchase: ShopifyPruchase) => {
      this.shopify
        .shopifyProductsPurchase(purchase)
        .then(() => this.toastr.success('Purchased new shopify licence'))
        .then(() => this.load());
    });
  }

  public resend(order: ShopifyOrder) {
    const input: ShopifyPruchase = {
      licence: order.licence,
      existingLicenceKey: order.licenceKey,
      sendMail: true,
      title: order.title,
      recipient: order.mail,
      firstName: order.firstName,
      lastName: order.lastName,
      language: order.language
    };
    const modalRef = this.modal.open(ShopifyPurchaseModalComponent, { size: 'lg' });
    modalRef.componentInstance.purchase = input;
    modalRef.componentInstance.onSubmit.subscribe((purchase: ShopifyPruchase) => {
      this.shopify
        .shopifyProductsPurchase(purchase)
        .then(() => this.toastr.success('Resend licence mail'))
        .then(() => this.load());
    });
  }

  public deleteSwalOptions(licence: ProductLicence) {
    const title = (licence as any).item?.title ?? licence.product;
    Swal.fire({
      title: 'Delete shopify licence?',
      html: `<p>Do you want to remove the licence template for <b style="font-weight: bold;">${title}</b>?</p>
      <p>This will not delete the product from shopify, but customers will not receive a licence key after buying it!</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-primary me-1',
        cancelButton: 'btn btn-danger'
      }
    }).then(result => {
      if (result.isConfirmed) {
        this.delete(licence);
      }
    });
  }

  public delete(licence: ProductLicence) {
    return this.shopify
      .licenceDelete(licence.uuid)
      .then(() => this.toastr.success('Deleted shopify licence'))
      .then(() => this.load());
  }

  private copy(item) {
    return JSON.parse(JSON.stringify(item));
  }

  private load(): Promise<any> {
    this.licences = null;
    this.products = null;
    this.mailTemplates = null;
    this.orders = null;
    return Promise.all([
      this.shopify.licenceList().then(l => (this.licences = l)),
      this.shopify.shopifyProductsList().then(p => (this.products = p)),
      this.shopify.mailTemplatesList().then(t => (this.mailTemplates = t)),
      this.shopify
        .shopifyProductsOrders()
        .then(o => (this.orders = o))
        .catch(err => Log.error('ShopifyComponent', 'Failed to fetch orders', err))
    ]).then(() => {
      this.licences.forEach(l => ((l as any).item = this.products.find(p => p.id === l.product)));
      this.unassignedProducts = this.products.filter(p => this.licences.every(l => p.id !== l.product));
      // this.orders?.forEach(o => (o['licenceRef'] = this.licences.find(l => (l.uuid = o.licence))));
    });
  }
}
